'use client';

import React from 'react';

import { Typography } from 'lighthouse';

import { SafeImage } from '@/widgets/SafeImage';
import { RatingStarIcon } from '@/widgets/StarRating';

import { calculateSatisfaction } from '@/utilities/calculateSatisfaction';
import { GenerateFilePreviewUrl } from '@/utilities/GenerateFilePreviewUrl';

import { VendorBannerProps } from './models';

import { Location, MessageText1, PercentageSquare, Verify } from 'iconsax-react';

const VENDOR_COVER_FALLBACK = '/images/vendor/vendor_default_cover_image.png';
const VENDOR_LOGO_FALLBACK = '/images/vendor/vendor_profile_default.png';

const VendorStatChips = ({ rate, ratesNumber, commentsNumber }: { rate: number; ratesNumber: number; commentsNumber: number }) => {
    const satisfaction = calculateSatisfaction(rate, ratesNumber);
    const reviewsCount = commentsNumber || ratesNumber;

    return (
        <div className='flex flex-wrap items-center justify-center gap-1.5 md:justify-start' aria-label='شاخص‌های فروشگاه'>
            <span
                className={`inline-flex h-7 items-center gap-1 rounded-full px-2 text-[11px] sm:h-8 sm:px-2.5 sm:text-xs ${satisfaction.className}`}>
                <RatingStarIcon size='sm' className='size-[13px]' />
                <span className='font-bold tabular-nums'>{rate.toFixed(1)}</span>
                <span className='hidden sm:inline'>امتیاز</span>
            </span>
            <span
                className={`inline-flex h-7 items-center gap-1 rounded-full px-2 text-[11px] sm:h-8 sm:px-2.5 sm:text-xs ${satisfaction.className}`}>
                <PercentageSquare size={13} variant='Bold' color='currentcolor' aria-hidden />
                <span className='font-bold tabular-nums'>{satisfaction.percent}٪</span>
                <span className='hidden sm:inline'>رضایت</span>
            </span>
            <span className='inline-flex h-7 items-center gap-1 rounded-full bg-primary/10 px-2 text-[11px] text-primary sm:h-8 sm:px-2.5 sm:text-xs'>
                <MessageText1 size={13} variant='Bold' color='currentcolor' aria-hidden />
                <span className='font-bold tabular-nums'>{reviewsCount.toLocaleString('fa-IR')}</span>
                <span className='hidden sm:inline'>نظر</span>
            </span>
        </div>
    );
};

export const VendorBanner = ({ vendor }: VendorBannerProps) => {
    if (!vendor) {
        return (
            <header className='mx-auto w-full' aria-busy='true' aria-label='در حال بارگذاری فروشگاه'>
                <div className='relative z-0 h-44 w-full animate-pulse bg-N200 md:h-72' />
                <div className='desktop-width relative z-10 -mt-14 px-3 sm:px-4'>
                    <div className='rounded-lg border border-solid border-N200 bg-white p-3 shadow-elevation1 sm:p-4'>
                        <div className='flex items-start gap-3'>
                            <div className='size-16 shrink-0 animate-pulse rounded-lg bg-N200 sm:size-20 md:size-24' />
                            <div className='min-w-0 flex-1 space-y-2'>
                                <div className='h-5 w-40 animate-pulse rounded-lg bg-N200 sm:h-6 sm:w-48' />
                                <div className='h-3.5 w-full animate-pulse rounded-lg bg-N100' />
                                <div className='h-3.5 w-3/4 animate-pulse rounded-lg bg-N100' />
                                <div className='flex gap-1.5 pt-1'>
                                    <div className='h-7 w-14 animate-pulse rounded-full bg-N100' />
                                    <div className='h-7 w-14 animate-pulse rounded-full bg-N100' />
                                    <div className='h-7 w-14 animate-pulse rounded-full bg-N100' />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
        );
    }

    const banner = GenerateFilePreviewUrl({ attachableId: vendor.uuid, attachment: 'STORE', type: 'COVER', isPrimary: true, sortOrder: 0 }).src;
    const logo = GenerateFilePreviewUrl({ attachableId: vendor.uuid, attachment: 'STORE', type: 'LOGO', isPrimary: true, sortOrder: 0 }).src;
    const hasVendorRate = typeof vendor.rate === 'number' && !Number.isNaN(vendor.rate) && vendor.ratesNumber > 0;
    const isVerified = vendor.status === 'ACTIVE';

    return (
        <header className='mx-auto w-full'>
            <div className='relative z-0 h-44 w-full overflow-hidden bg-gradient-to-br from-primary via-secondary to-primary md:h-72'>
                <SafeImage
                    src={banner}
                    alt={`تصویر کاور ${vendor.name}`}
                    fallback={VENDOR_COVER_FALLBACK}
                    fill
                    priority
                    sizes='100vw'
                    className='object-cover'
                />
                <div className='pointer-events-none absolute inset-0 bg-gradient-to-t from-black/65 via-black/25 to-black/5' aria-hidden />
            </div>

            <div className='desktop-width relative z-10 -mt-14 px-3 sm:px-4'>
                <div className='rounded-lg border border-solid border-white/70 bg-white p-3 shadow-elevation2 sm:p-4'>
                    <div className='flex items-start gap-3 sm:gap-4'>
                        <div className='relative size-16 shrink-0 overflow-hidden rounded-lg ring-2 ring-white sm:size-20 md:size-24'>
                            <SafeImage
                                src={logo}
                                alt={`لوگوی ${vendor.name}`}
                                fallback={VENDOR_LOGO_FALLBACK}
                                fill
                                priority
                                sizes='96px'
                                className='object-cover'
                            />
                        </div>

                        <div className='min-w-0 flex-1'>
                            <div className='flex flex-wrap items-center gap-1.5'>
                                <Typography component='h1' className='text-base font-bold leading-6 text-blackHighEmphasis sm:text-lg md:text-xl'>
                                    {vendor.name}
                                </Typography>
                                {isVerified && (
                                    <span
                                        className='inline-flex items-center gap-0.5 rounded-full bg-success/10 px-1.5 py-0.5 text-[10px] font-medium
                                            text-success sm:text-[11px]'>
                                        <Verify size={12} variant='Bold' color='currentcolor' aria-hidden />
                                        تایید‌شده
                                    </span>
                                )}
                            </div>

                            <Typography className='mt-1 line-clamp-2 text-xs leading-5 text-blackMediumEmphasis sm:text-sm sm:leading-6'>
                                {vendor.shortDescription || 'فروشگاه اختصاصی آزکی‌سرویس'}
                            </Typography>

                            {vendor.address ? (
                                <p className='mt-1.5 flex items-start gap-1 text-[11px] leading-5 text-blackLowEmphasis sm:text-xs'>
                                    <Location size={13} variant='Bold' color='currentcolor' className='mt-0.5 shrink-0' aria-hidden />
                                    <span className='line-clamp-2 sm:line-clamp-1'>
                                        {[vendor.cityName?.trim(), vendor.address].filter(Boolean).join('، ')}
                                    </span>
                                </p>
                            ) : null}

                            <div className='mt-2'>
                                {hasVendorRate ? (
                                    <VendorStatChips rate={vendor.rate} ratesNumber={vendor.ratesNumber} commentsNumber={vendor.commentsNumber} />
                                ) : (
                                    <span className='inline-flex h-7 items-center rounded-full bg-gray-50 px-2.5 text-[11px] text-gray-500 sm:text-xs'>
                                        هنوز نظری ثبت نشده
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};
